import { AdviceTodayResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/advice/advice-today.response';
import { UserAlertResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/smart-alerts/user-alert.response';
import { GreenLightVerdictResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/green-light/green-light.response';
import { PlantListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/plant-list.response';
import { ThermalConditionBadge } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/thermal-progress.response';
import { LineIconName } from '../../../@icons/line-icons';
import { appLocale } from '../../../@commons/pipes/vw-date.pipe';
import { PlantAction } from '../../../@commons/@services/data/garden-todo.store';
import { CultivationGrammarResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/cultivation/cultivation-grammar.response';
import { NounForm, NounNumber, nounForm, nounList } from '../../../@commons/utils/noun-phrase.util';
import { WeatherCondition } from '../weather-moon-band/weather-moon-band.model';

/** Familles du catalogue (`catalogue-mot-du-jour.csv`). */
export type DayFamily =
  | 'securite'
  | 'meteo'
  | 'eau'
  | 'sol'
  | 'semis_stock'
  | 'recolte'
  | 'croissance'
  | 'maladie'
  | 'entretien'
  | 'calme'
  | 'hors_saison'
  | 'premier_pas';

/** Les familles « jour calme » : jamais deux jours de suite, une fois par semaine au plus. */
export const CALM_FAMILIES: readonly DayFamily[] = ['calme', 'hors_saison', 'premier_pas'];

/** Fond et couleur du message : ce qu'il annonce. */
export type DayTone = 'frost' | 'water' | 'wind' | 'go' | 'harvest' | 'alert' | 'calm';

/**
 * Ce que fait le bouton du message. `DONE` répond « C'est fait » ; `PICKED`
 * ouvre la récolte ; `OPEN` mène à l'écran du geste (`link`) ; `ALERTS`
 * demande la permission des alertes ; `INSTALL` ouvre la boutique de l'app.
 */
export type DayAction = 'DONE' | 'PICKED' | 'OPEN' | 'ALERTS' | 'INSTALL';

/** Un texte à traduire : sa clé et ses valeurs, ou un texte déjà écrit par le serveur. */
export interface DayText {
  key?: string;
  params?: Record<string, string | number>;
  /** Déjà traduit (conseil ou alerte du serveur). */
  text?: string;
  /** Ce morceau est calculé, pas constaté : la pastille « estimé » le suit. */
  estimated?: boolean;
  /** L'accord du groupe nommé : la clé prend sa variante (`_one`, `_f`) quand elle existe. */
  form?: NounForm | null;
  /** Le texte à dire quand la clé n'existe pas (une forme propre à quelques règles seulement). */
  fallback?: DayText;
}

/** Une plante (ou une culture) dont parle le message. */
export interface DayPlant {
  /** Plante du jardin ; absente pour une culture du catalogue (Feu vert d'un sachet). */
  uid: string | null;
  name: string;
  imageUrl: string | null;
  location: string | null;
  /** La culture, pour grouper par culture une règle dont le geste en dépend. */
  cultivationUid?: string;
  /** Date de plantation, pour départager deux messages : la plus jeune d'abord. */
  plantedAt?: string | null;
  /** La plante complète quand l'accueil l'a : la récolte et « Plus là » en ont besoin. */
  plant?: PlantListResponse;
  /** Le nom dans une phrase, par langue (« tes pommes de terre ») ; absent, la phrase reste sans accord. */
  grammar?: CultivationGrammarResponse | null;
}

/** Le message du jour, prêt à dessiner (`vw-day-message`) et à répondre (`vw-advice-reply`). */
export interface DayMessage {
  /** Unique pour la journée : code et plantes. */
  key: string;
  /** Code renvoyé avec la réponse : déclencheur du serveur ou code du catalogue. */
  code: string;
  family: DayFamily;
  priority: number;
  tone: DayTone;
  plants: DayPlant[];
  /** La ligne du haut : pictogramme et signal (« Gel cette nuit, -1 °C »). */
  signal: (DayText & { icon: LineIconName }) | null;
  /** Le temps que ce signal annonce déjà : la bande météo ne le redit pas. */
  announces: WeatherCondition | null;
  /** D'où vient le signal, en gris (« prévu à 06:00 »). */
  source: DayText | null;
  /** Le titre, en deux formes : la courte quand la longue dépasse trois lignes. */
  title: DayText;
  shortTitle: DayText;
  /** En ligne de geste, l'impératif court (« Récolte tes radis ») ; à défaut, le titre. */
  rowTitle: DayText;
  /** Après « C'est fait », le geste accordé aux plantes (« Pommes de terre couvertes, c'est noté. ») ; `null` sans nom relu. */
  noted: DayText | null;
  /** La raison, en morceaux mis bout à bout. */
  reason: DayText[];
  /** En ligne de geste : la pastille courte (« Feu vert ») et la raison courte (« Terre ressuyée », « Planche 2 »). */
  tag: DayText;
  rowReason: DayText | null;
  /** Ce qui est calculé et non constaté : conditionnel et pastille « estimé ». */
  estimated: boolean;
  /** Une phrase de plus, en gris : ce qu'on ne sait pas (météo pas à jour). */
  note: DayText | null;
  /** Le geste ; sans lui, le message informe et ne garde que « … ». */
  button: { key: string; action: DayAction } | null;
  /** Écran du geste pour `OPEN`, ou fiche de l'alerte. */
  link: string | null;
  /** Le jour de l'événement (gel, fenêtre), pour départager : le plus proche d'abord. */
  eventDate: string | null;
  /** Alerte du serveur à passer « lue » quand on y répond. */
  alertUid?: string;
  /** Ordre de lecture du catalogue : le gel devant le vent, à priorité égale. */
  rank: number;
  /** Ne se montre qu'une fois sur cet appareil (alertes, installer l'app). */
  once?: boolean;
  /** Parle de tout le jardin sans nommer de plante (« Tes 2 plantes font une pause ») : il les nomme toutes. */
  wholeGarden?: boolean;
}

/** Les plantes du jardin telles que l'accueil les connaît, pour les messages qui partent d'elles. */
export interface DayThermalItem {
  plantUid: string;
  cultivationName: string;
  locationName: string;
  conditionBadge: ThermalConditionBadge | null;
}

/** Ce que le moteur lit : uniquement ce que l'accueil a déjà chargé. */
export interface DayContext {
  now: Date;
  lang: string;
  advice: AdviceTodayResponse | null;
  /** Alertes non lues du compte. */
  alerts: UserAlertResponse[];
  /** Verdicts Feu vert des cultures de la personne. */
  greenLights: GreenLightVerdictResponse[];
  /** Plantes dont l'étape manuelle attend d'être confirmée. */
  plantActions: PlantAction[];
  /** Plantes vivantes du jardin ; `null` si elles ne sont pas chargées. */
  plants: PlantListResponse[] | null;
  thermal: DayThermalItem[];
  /** Totaux du compte ; `null` s'ils ne sont pas connus. */
  counts: { seeds: number; plants: number; zones: number } | null;
  /** Les zones du jardin (pour « De la place sur Planche 2 »). */
  zones?: { uid: string; name: string }[];
  /** Les codes montrés hier sur cet appareil (« Pas besoin d'arroser » clôt la soif d'hier) ; à défaut, le moteur les lit dans l'historique. */
  recentCodes?: string[];
  /** L'étape en plus du premier lancement, encore à faire ou non (alertes dans l'app, l'app dans le navigateur). */
  setup?: { kind: 'alerts' | 'install'; done: boolean } | null;
  /** Réponses données sans réseau, pas encore parties : elles comptent comme répondues. */
  pendingReplies?: { triggerCode: string; plantUid?: string | null; day: string }[];
}

/** Une ligne de l'historique de cet appareil : ce qui a été montré tel jour. */
export interface DayHistoryEntry {
  /** AAAA-MM-JJ */
  day: string;
  key: string;
  code: string;
  plants: string[];
  /** Le message était en tête. */
  head: boolean;
}

/** Le choix du jour : le message principal, deux gestes au plus, le reste pour la quinzaine. */
export interface DayChoice {
  head: DayMessage | null;
  others: DayMessage[];
  rest: DayMessage[];
  /** La météo est à jour : une prévision du jour local qui couvre aujourd'hui (`isWeatherFresh`). */
  weatherFresh: boolean;
  /** Un jour calme, une question d'exemple à Willy tirée des graines (« Je sème mes fèves maintenant ou en février ? »). */
  willy: DayText | null;
}


/** Le jour local, « AAAA-MM-JJ ». */
export function localDay(date: Date): string {
  return date.toLocaleDateString('en-CA');
}

/**
 * La météo du conseil est-elle à jour ? La prévision est du jour (le relevé
 * tourne chaque nuit vers 3 h) et elle couvre aujourd'hui. Celle d'hier est
 * ancienne, même lue à 1 h du matin.
 */
export function isWeatherFresh(advice: AdviceTodayResponse | null | undefined, now: Date): boolean {
  if (!advice?.forecastAt) return false;
  const at = new Date(advice.forecastAt);
  if (Number.isNaN(at.getTime())) return false;
  const today = localDay(now);
  return localDay(at) === today && (advice.forecast ?? []).some((day) => day.date === today);
}

/**
 * Les noms dans une phrase : « pomme de terre et tomate ». Minuscule en tête
 * (le nom vient d'un titre, « Pomme de terre »), sauf en allemand, où les noms
 * gardent leur majuscule.
 */
export function namesInSentence(names: string[], lang: string): string {
  const language = (lang || 'fr').toLowerCase();
  const words = [...new Set(names.filter(Boolean))].map((name) =>
    language === 'de' ? name : name.charAt(0).toLocaleLowerCase(language) + name.slice(1),
  );
  return new Intl.ListFormat(appLocale(language), { style: 'long', type: 'conjunction' }).format(words);
}

/**
 * Les plantes dans la phrase. Quand chaque nom est relu pour la langue :
 * `plants` (« tes pommes de terre et ton ail »), `plantsSubject` (le sujet,
 * pour l'allemand), `bare` (le nom seul) et l'accord du groupe. Sinon les
 * noms seuls (`names`), et la phrase prend sa forme sans accord.
 */
export function plantWords(
  plants: DayPlant[],
  lang: string,
  number: NounNumber = 'plural',
): { agreed: boolean; form: NounForm | null; params: Record<string, string> } {
  const names = namesInSentence(plants.map((p) => p.name), lang);
  const grammars = plants.map((p) => p.grammar);
  const phrase = plants.length ? nounList(grammars, lang, { number }) : null;
  if (!phrase) return { agreed: false, form: null, params: { names, plants: names } };
  return {
    agreed: true,
    form: nounForm(grammars, lang, number),
    params: {
      names,
      plants: phrase,
      plantsSubject: nounList(grammars, lang, { number, role: 'subject' })!,
      bare: nounList(grammars, lang, { number, determiner: 'none' })!,
    },
  };
}
